import { payrollPersonType } from './payrollPeople.js';
import { LOAN_TYPE_LABEL, isReceivable } from './payrollLoans.js';
import { isHoursReportView } from './payrollHoursReport.js';
/** The spec's categories, in the spec's order — plus "Reimbursements has its own category" (§20c). */
export const REPORT_CATEGORIES = [
    { key: 'PAYROLL', label: 'Payroll' },
    { key: 'HOURS', label: 'Hours & timesheet' },
    { key: 'PAYMENT', label: 'Payment' },
    { key: 'ADJUSTMENT', label: 'Adjustment' },
    { key: 'REIMBURSEMENTS', label: 'Reimbursements' },
    { key: 'PEOPLE', label: 'People' },
    { key: 'AUDIT', label: 'Audit reports' },
];
/** Every report the center offers. Only reports that exist are listed — never a placeholder. */
export const PAYROLL_REPORTS = [
    {
        key: 'registers',
        name: 'Check register',
        category: 'PAYROLL',
        description: 'Every finalised check register, exactly as frozen when its pay run was submitted — never a preview.',
    },
    {
        key: 'runs',
        name: 'Pay run summary',
        category: 'PAYROLL',
        description: 'Each pay run per entity and currency: people, hours, base pay, adjustments and net pay — never summed across currencies.',
    },
    {
        key: 'hours',
        name: 'Hours report',
        category: 'HOURS',
        description: 'Hours by person or client against capped hours, overtime and weekend hours, and every entry.',
    },
    {
        key: 'payments',
        name: 'Payment history',
        category: 'PAYMENT',
        description: 'What was paid in a date range, per person and pay period, with additions and deductions.',
    },
    {
        key: 'owed',
        name: 'Amounts owed',
        category: 'PAYMENT',
        description: 'Pay approved but not yet sent, and hours worked in periods no pay run covers yet.',
    },
    {
        key: 'alladj',
        name: 'All advances, loans & adjustments',
        category: 'ADJUSTMENT',
        description: 'Every loan and salary advance and where it stands, with every bonus or other adjustment filed for the pay periods you pick — in one list.',
    },
    {
        key: 'loans',
        name: 'Loan & advance history',
        category: 'ADJUSTMENT',
        description: 'Every loan and salary advance: deducted, skipped or not deducted each pay run, and what is left.',
    },
    {
        key: 'receivables',
        name: 'Receivables',
        category: 'ADJUSTMENT',
        description: 'What people still owe Remote Talent from loans and salary advances, and what the next pay run collects.',
    },
    {
        key: 'skips',
        name: 'Skipped cut-offs',
        category: 'ADJUSTMENT',
        description: 'Every pay run a loan or advance repayment was skipped for, and the reason given.',
    },
    {
        key: 'collected',
        name: 'Fully collected',
        category: 'ADJUSTMENT',
        description: 'Loans and salary advances paid back in full, and the pay run that cleared each one.',
    },
    {
        key: 'runlines',
        name: 'Deductions & additions this pay run',
        category: 'ADJUSTMENT',
        description: 'One pay run: who had what added or deducted, which loan repayments were skipped and why, and who has nothing set.',
    },
    {
        key: 'adjustments',
        name: 'Bonuses & other adjustments',
        category: 'ADJUSTMENT',
        description: 'Bonuses, overtime, commission and other deductions filed on pay, and whether each is on a pay run yet.',
    },
    {
        key: 'reimbursements',
        name: 'Reimbursements',
        category: 'REIMBURSEMENTS',
        description: 'Expense and prize claims submitted in a date range: amount, status, and when and how each was paid.',
    },
    {
        key: 'people',
        name: 'People & rates',
        category: 'PEOPLE',
        description: 'Everyone on payroll with their entity, status, hourly and weekend rate, and hours target.',
    },
    {
        key: 'audit',
        name: 'Audit trail',
        category: 'AUDIT',
        description: 'Every change made in Payroll — who, when, what — from pay runs and loans to disputes, access and Wise.',
    },
];
export const reportDef = (key) => PAYROLL_REPORTS.find((r) => r.key === key) ?? null;
export const categoryLabel = (key) => REPORT_CATEGORIES.find((c) => c.key === key)?.label ?? key;
/** The categories that have at least one report, in the spec's order — an empty category is not offered. */
export function reportCategoriesInUse() {
    return REPORT_CATEGORIES.map((c) => ({ ...c, count: PAYROLL_REPORTS.filter((r) => r.category === c.key).length })).filter((c) => c.count > 0);
}
/**
 * "Search reports": every word must match the report's name, description or category, ignoring
 * case. An empty search lists every report.
 */
export function searchReports(query) {
    const words = query.toLowerCase().split(/\s+/).filter(Boolean);
    if (!words.length)
        return [...PAYROLL_REPORTS];
    return PAYROLL_REPORTS.filter((r) => {
        const hay = `${r.name} ${r.description} ${categoryLabel(r.category)}`.toLowerCase();
        return words.every((w) => hay.includes(w));
    });
}
export const RECENT_REPORTS_MAX = 25;
/**
 * "Recent reports": opening a report puts it first with the time it was opened; each report appears
 * once; the list keeps the newest 25. Anything that isn't a known report (an old or tampered entry in
 * the browser) is dropped.
 */
export function recordRecentReport(list, key, at) {
    return [{ key, openedAt: at }, ...list.filter((r) => r.key !== key && reportDef(r.key) != null)].slice(0, RECENT_REPORTS_MAX);
}
/** Read back what the browser stored, keeping only well-formed entries for reports that exist. */
export function parseRecentReports(raw) {
    if (!Array.isArray(raw))
        return [];
    return raw
        .filter((r) => !!r && typeof r === 'object' && reportDef(r.key) != null && typeof r.openedAt === 'string')
        .slice(0, RECENT_REPORTS_MAX);
}
export const REPORT_PAGE_SIZE = 25;
/** "25 per page": the rows on a page (1-based, clamped to the pages that exist) and how many pages. */
export function reportPage(rows, page, size = REPORT_PAGE_SIZE) {
    const pages = Math.max(1, Math.ceil(rows.length / size));
    const p = Math.min(Math.max(1, Math.floor(page) || 1), pages);
    return { rows: rows.slice((p - 1) * size, p * size), page: p, pages };
}
// ── People & rates (spec §20c "People & rates") ────────────────────────────────────────────────
const PERSON_STATUS_LABEL = {
    ACTIVE: 'Active',
    ONBOARDING: 'Onboarding',
    FINISHING: 'Finishing',
    INACTIVE: 'Inactive',
};
/** One row per person, shaped from Payroll → People's own data — the same figures People shows. */
export function peopleRatesRows(people, entities) {
    const byId = new Map(entities.map((e) => [e.id, e]));
    return people
        .map((p) => {
        const e = p.entity ? byId.get(p.entity.entityId) : undefined;
        return {
            userId: p.userId,
            employeeId: p.employeeId,
            name: p.fullName,
            type: payrollPersonType(e?.code),
            entity: e ? `${e.name}${p.entity?.suggested ? ' (suggested)' : ''}` : 'No entity',
            status: PERSON_STATUS_LABEL[p.status],
            currency: p.payCurrency,
            rate: p.payRate,
            weekendRate: p.weekendRate,
            hoursTarget: p.expectedHours,
            activeClients: p.activeClients.length,
        };
    })
        .sort((a, b) => a.name.localeCompare(b.name));
}
/** A cell a spreadsheet can't run: quoted when needed, and a leading =, +, @ or tab defused. */
const csvCell = (v) => {
    let s = v == null ? '' : String(v);
    if (typeof v === 'string' && /^[=+@\t\r]/.test(s))
        s = `'${s}`;
    return /[",\r\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
};
export function peopleRatesCsv(rows) {
    const head = ['Employee no.', 'Name', 'Type', 'Entity', 'Status', 'Currency', 'Rate per hour', 'Weekend rate', 'Hours target', 'Active clients'];
    const lines = rows.map((r) => [r.employeeId ?? '', r.name, r.type, r.entity, r.status, r.currency, r.rate ?? '', r.weekendRate ?? '', r.hoursTarget ?? '', r.activeClients]);
    return [head, ...lines].map((l) => l.map(csvCell).join(',')).join('\n') + '\n';
}
const RUN_STATUS_LABEL = { DRAFT: 'Draft', APPROVED: 'Approved', PUSHED: 'Submitted', COMPLETED: 'Paid', FAILED: 'Failed' };
export const runStatusLabel = (s) => RUN_STATUS_LABEL[s] ?? s;
const cents = (n) => Math.round(n * 100) / 100;
/**
 * One row per pay run × entity × currency, from the STORED lines (amount = base + adjustments), newest
 * run first, entities in their own order. ⛔ Never one figure across currencies.
 */
export function payRunSummaryRows(items, entityOf, entities) {
    const order = new Map(entities.map((e, i) => [e.id, i]));
    const nameOf = new Map(entities.map((e) => [e.id, e.name]));
    const groups = new Map();
    for (const it of items) {
        const entityId = entityOf.get(it.userId) ?? null;
        const key = `${it.runId}|${entityId ?? ''}|${it.currency}`;
        let g = groups.get(key);
        if (!g) {
            g = {
                runId: it.runId,
                periodStart: it.periodStart,
                periodEnd: it.periodEnd,
                status: runStatusLabel(it.runStatus),
                entityId,
                entityName: entityId ? (nameOf.get(entityId) ?? 'Unknown entity') : 'No entity',
                currency: it.currency,
                people: 0,
                hours: 0,
                basePay: 0,
                adjustments: 0,
                net: 0,
                users: new Set(),
            };
            groups.set(key, g);
        }
        g.users.add(it.userId);
        g.hours = cents(g.hours + it.hours);
        g.basePay = cents(g.basePay + it.baseAmount);
        g.adjustments = cents(g.adjustments + it.adjustmentAmount);
        g.net = cents(g.net + it.amount);
    }
    return [...groups.values()]
        .map(({ users, ...r }) => ({ ...r, people: users.size }))
        .sort((a, b) => b.periodStart.localeCompare(a.periodStart) ||
        (order.get(a.entityId ?? '') ?? 999) - (order.get(b.entityId ?? '') ?? 999) ||
        a.currency.localeCompare(b.currency));
}
/** Per currency across the rows shown — pesos and rand are never added together. */
export function payRunSummaryTotals(rows) {
    const by = new Map();
    for (const r of rows) {
        const t = by.get(r.currency) ?? { currency: r.currency, hours: 0, basePay: 0, adjustments: 0, net: 0 };
        t.hours = cents(t.hours + r.hours);
        t.basePay = cents(t.basePay + r.basePay);
        t.adjustments = cents(t.adjustments + r.adjustments);
        t.net = cents(t.net + r.net);
        by.set(r.currency, t);
    }
    return [...by.values()].sort((a, b) => a.currency.localeCompare(b.currency));
}
export function payRunSummaryCsv(rows, runName) {
    const head = ['Pay run', 'Status', 'Entity', 'Currency', 'People', 'Hours', 'Base pay', 'Adjustments', 'Net pay'];
    const lines = rows.map((r) => [runName(r.periodStart, r.periodEnd), r.status, r.entityName, r.currency, r.people, r.hours, r.basePay, r.adjustments, r.net]);
    return [head, ...lines].map((l) => l.map(csvCell).join(',')).join('\n') + '\n';
}
/** "PAYROLL_LOAN_SKIP" → "Loan skip"; the few that read badly spelled out by hand. */
const AUDIT_LABEL = {
    USER_UPDATE: 'Pay setup changed',
    MANUAL_ENTRY_APPROVE: 'Time added by payroll',
    WISE_CONNECTION_CHANGE: 'Wise connection changed',
    PAYROLL_PUSH: 'Pay run sent',
    PAYROLL_APPROVE: 'Pay run approved',
    PAYROLL_VA_REVIEW: 'Staff pay check',
};
export function auditActionLabel(action) {
    if (AUDIT_LABEL[action])
        return AUDIT_LABEL[action];
    const words = action.replace(/^PAYROLL_/, '').toLowerCase().split('_').filter(Boolean).join(' ');
    return words ? words.charAt(0).toUpperCase() + words.slice(1) : action;
}
/**
 * An entry's metadata as one readable line ("decision: QUERIED · note: …"), nested values flattened,
 * capped at 240 characters. ⛔ Keys that could carry a credential are never shown, whatever they hold.
 */
export function auditDetails(metadata) {
    if (metadata == null || typeof metadata !== 'object')
        return metadata == null ? '' : String(metadata).slice(0, 240);
    const parts = [];
    const walk = (v, path) => {
        if (parts.join(' · ').length > 240)
            return;
        if (/token|secret|password|apikey|api_key/i.test(path))
            return;
        if (v == null)
            return;
        if (Array.isArray(v)) {
            parts.push(`${path}: ${v.map((x) => (typeof x === 'object' ? JSON.stringify(x) : String(x))).join(', ')}`);
        }
        else if (typeof v === 'object') {
            for (const [k, x] of Object.entries(v))
                walk(x, path ? `${path}.${k}` : k);
        }
        else {
            parts.push(`${path}: ${String(v)}`);
        }
    };
    walk(metadata, '');
    const s = parts.join(' · ');
    return s.length > 240 ? `${s.slice(0, 239)}…` : s;
}
export function payrollAuditCsv(rows) {
    // Spec §12's columns. An entry that changed several fields lists them in order, "; " between.
    const head = ['When', 'Who', 'Source', 'Action', 'Record', 'Person', 'Field', 'Before', 'After', 'Details'];
    const many = (r, pick) => r.changes.map(pick).join('; ');
    const lines = rows.map((r) => [r.at, r.actorName ?? '', r.source, r.actionLabel, r.record, r.personName ?? '', many(r, (c) => c.field), many(r, (c) => c.before), many(r, (c) => c.after), r.details]);
    return [head, ...lines].map((l) => l.map(csvCell).join(',')).join('\n') + '\n';
}
/** Every report but the check register can be saved as a view (a register is already saved). */
/** A saved view keeps a report's FILTERS. Registers are documents, and "this pay run" is picked each time — neither has filters to keep. */
export const SAVEABLE_REPORTS = PAYROLL_REPORTS.map((r) => r.key).filter((k) => k !== 'registers' && k !== 'runlines');
const str = (v, max) => (typeof v === 'string' ? v.slice(0, max) : undefined);
/**
 * What a saved view may hold, and nothing else: unknown keys are dropped and malformed values
 * ignored, so a stored view can never feed a report something it doesn't understand.
 */
export function cleanSavedReportParams(raw) {
    const o = raw && typeof raw === 'object' && !Array.isArray(raw) ? raw : {};
    const out = {};
    const r = o.range;
    if (r && typeof r === 'object') {
        const preset = str(r.preset, 20);
        const from = str(r.from, 40);
        const to = str(r.to, 40);
        if (preset && from && to && !Number.isNaN(Date.parse(from)) && !Number.isNaN(Date.parse(to))) {
            out.range = { preset, from, to, label: str(r.label, 60) ?? '' };
        }
    }
    if (o.groupBy === 'none' || o.groupBy === 'member' || o.groupBy === 'period')
        out.groupBy = o.groupBy;
    if (Array.isArray(o.columns))
        out.columns = o.columns.filter((c) => typeof c === 'string').slice(0, 30).map((c) => c.slice(0, 40));
    const h = o.hours;
    if (h && typeof h === 'object' && isHoursReportView(h.view)) {
        out.hours = { view: h.view, search: str(h.search, 200) ?? '', manager: str(h.manager, 200) ?? '', client: str(h.client, 200) ?? '', note: str(h.note, 200) ?? '' };
    }
    const s = str(o.search, 200);
    if (s !== undefined)
        out.search = s;
    return out;
}
const standing = (l, clears) => ({
    loanId: l.id,
    userId: l.userId,
    personName: l.personName,
    employeeId: l.employeeId,
    entityId: l.entityId,
    type: LOAN_TYPE_LABEL[l.type] ?? l.type,
    currency: l.currency,
    original: l.amount,
    collected: l.collected,
    outstanding: l.outstanding,
    nextDeduction: l.skippedNext ? 0 : l.nextDeduction,
    skippedNext: !!l.skippedNext,
    clearsPeriodStart: clears,
});
/** Receivables: what people still owe (the Adjustments tab's own rule), biggest first. */
export function receivableRows(loans) {
    return loans.filter(isReceivable).map((l) => standing(l, l.projectedClearedPeriodStart)).sort((a, b) => b.outstanding - a.outstanding);
}
/** Fully collected: every loan and advance paid back in full, with the pay run that cleared it. */
export function fullyCollectedRows(loans) {
    return loans
        .filter((l) => l.status === 'COMPLETED')
        .map((l) => {
        const deducted = l.history.filter((h) => h.kind === 'DEDUCTED').map((h) => h.periodStart).sort();
        return standing(l, deducted.at(-1) ?? null);
    })
        .sort((a, b) => (b.clearsPeriodStart ?? '').localeCompare(a.clearsPeriodStart ?? ''));
}
/** Skipped cut-offs: every pay run a repayment was skipped for, and why — newest first. */
export function skippedCutoffRows(loans) {
    return loans
        .flatMap((l) => l.history
        .filter((h) => h.kind === 'SKIPPED')
        .map((h) => ({
        loanId: l.id,
        userId: l.userId,
        personName: l.personName,
        entityId: l.entityId,
        type: LOAN_TYPE_LABEL[l.type] ?? l.type,
        currency: l.currency,
        periodStart: h.periodStart,
        reason: h.text.replace(/^Skipped:\s*/, ''),
        at: h.at,
    })))
        .sort((a, b) => b.periodStart.localeCompare(a.periodStart) || a.personName.localeCompare(b.personName));
}
const ADJUSTMENT_KIND_LABEL = { BONUS: 'Bonus', OVERTIME: 'Overtime', COMMISSION: 'Commission', LOAN_DEDUCTION: 'Other deduction', OTHER_ADDITION: 'Other addition', OTHER_DEDUCTION: 'Other deduction', REFERRAL_BONUS: 'Referral bonus', PTO: 'Paid time off', REIMBURSEMENT: 'Reimbursement' };
export const adjustmentKindLabel = (kind) => ADJUSTMENT_KIND_LABEL[kind] ?? kind.charAt(0) + kind.slice(1).toLowerCase().replace(/_/g, ' ');
/** The kinds that take money off pay. Anything else adds to it. */
export const isDeductionKind = (kind) => kind === 'LOAN_DEDUCTION' || kind.endsWith('_DEDUCTION');
const REIMBURSEMENT_STATUS_LABEL = { SUBMITTED: 'Submitted', APPROVED: 'Approved', REJECTED: 'Rejected', PAID: 'Paid', WITHDRAWN: 'Withdrawn' };
export const reimbursementStatusLabel = (s) => REIMBURSEMENT_STATUS_LABEL[s] ?? s;
/** Per currency, and per status within it — never one figure across currencies. */
export function reimbursementTotals(rows) {
    const by = new Map();
    for (const r of rows) {
        const k = `${r.currency}|${r.status}`;
        const t = by.get(k) ?? { currency: r.currency, status: r.status, amount: 0, count: 0 };
        t.amount = cents(t.amount + r.amount);
        t.count += 1;
        by.set(k, t);
    }
    return [...by.values()].sort((a, b) => a.currency.localeCompare(b.currency) || a.status.localeCompare(b.status));
}
/** One CSV builder for the simple tables, so quoting and formula-defusing are the same everywhere. */
export function tableCsv(head, rows) {
    return [head, ...rows].map((l) => l.map(csvCell).join(',')).join('\n') + '\n';
}
/**
 * One list of everything in the category: every loan and salary advance (whatever the dates — a loan
 * is not "in" a date range), then the bonuses and other adjustments filed for pay periods in the range.
 * Sorted by person; a person's loans come before their adjustments. `runName` names a pay run from its
 * start. ⛔ Nothing is added up across currencies here — each row is in its own.
 */
export function allAdjustmentsRows(standing, adjustments, runName, money) {
    const owing = standing.receivables.map((l) => ({
        key: `loan:${l.loanId}`,
        userId: l.userId,
        personName: l.personName,
        entityId: l.entityId,
        what: l.type,
        currency: l.currency,
        amount: l.original,
        standing: `${money(l.collected, l.currency)} collected · ${money(l.outstanding, l.currency)} left` +
            (l.skippedNext ? ' · next deduction skipped' : l.nextDeduction > 0 ? ` · next ${money(l.nextDeduction, l.currency)}` : ''),
        open: true,
        source: 'LOAN',
    }));
    const done = standing.collected.map((l) => ({
        key: `loan:${l.loanId}`,
        userId: l.userId,
        personName: l.personName,
        entityId: l.entityId,
        what: l.type,
        currency: l.currency,
        amount: l.original,
        standing: l.clearsPeriodStart ? `Fully collected in the ${runName(l.clearsPeriodStart)} pay run` : 'Fully collected',
        open: false,
        source: 'LOAN',
    }));
    const filed = adjustments.map((a) => ({
        key: `adj:${a.id}`,
        userId: a.userId,
        personName: a.personName,
        entityId: a.entityId,
        what: a.kindLabel,
        currency: a.currency,
        amount: a.amount,
        standing: a.applied ? `On the ${runName(a.periodStart)} pay run` : `Waiting for the ${runName(a.periodStart)} pay run`,
        open: !a.applied,
        source: 'ADJUSTMENT',
    }));
    const order = { LOAN: 0, ADJUSTMENT: 1 };
    return [...owing, ...done, ...filed].sort((a, b) => a.personName.localeCompare(b.personName) || order[a.source] - order[b.source] || Number(b.open) - Number(a.open));
}
export function allAdjustmentsCsv(rows) {
    return tableCsv(['Person', 'What', 'Currency', 'Amount', 'Where it stands', 'Still open'], rows.map((r) => [r.personName, r.what, r.currency, r.amount, r.standing, r.open ? 'yes' : '']));
}
//# sourceMappingURL=payrollReportCenter.js.map